import { EM_DASH } from "constants/index";

import { FC } from "react";

import { CalendarToday } from "@material-ui/icons";
import { Paper, portexColor, TextInput } from "@portex-pro/ui-components";
import { ShipperQuoteRequest } from "api/rest/quote-requests";
import formatAddress from "app/pages/shipments/utils/formatAddress";
import AttachmentsListContainer from "components/file-uploads/AttachmentsListContainer";
import RecipientTags from "components/RecipientTags";
import Text from "components/Text";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import { DateTime } from "luxon";
import { mapDriverPreferenceToCopy } from "pages/shipper/utils/mapDriverPreferenceToCopy";
import { useTranslation } from "react-i18next";
import { TrailerTypeEnum } from "types/TrailerType";

interface EmailPreviewSectionProps {
  quoteRequest: ShipperQuoteRequest;
}

const getTrailerTypeCopy = (trailerType: TrailerTypeEnum) => {
  switch (trailerType) {
    case TrailerTypeEnum.RGN:
      return "Removable Gooseneck (RGN)";
    case TrailerTypeEnum.NA_PARTNERS_CHOICE:
      return "N/A - Partner's Choice";
    default: {
      return startCase(capitalize(trailerType));
    }
  }
};

/**
 * @todo Refactor the FTL review step to use this component
 * @todo Refactor this component to work for all different mode types
 */
const EmailPreviewSection: FC<EmailPreviewSectionProps> = (props) => {
  const { quoteRequest } = props;
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);

  return (
    <div className="mb-6">
      <Paper variant="outlined" className="bg-gray-50 p-4">
        <div className="flex flex-col space-y-3 mb-4">
          <div className="flex items-center">
            <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.bcc")}:</span>
            <RecipientTags
              recipients={quoteRequest.partners.map((partner) => partner.company_name ?? partner.email) ?? []}
              limit={3}
              customStyle={{ backgroundColor: "#F3F4F5" }}
            />
          </div>
          <div className="flex items-center">
            <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.cc")}:</span>
            <RecipientTags
              recipients={quoteRequest.teammates.map((teammate) => teammate.email) ?? []}
              limit={3}
              customStyle={{ backgroundColor: "#F3F4F5" }}
            />
          </div>
          <div className="flex items-center mb-4">
            <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.subject")}:</span>
            <TextInput value={quoteRequest.email_metadata?.subject} fullWidth className="bg-white" />
          </div>
        </div>

        {/* {!!releaseQuoteRequestFiles && (
          <div className="flex pb-4 justify-end">
            <AttachFileFromButtonContainer />
          </div>
        )} */}

        <Paper variant="outlined">
          <div className="flex justify-between p-4 items-center border-b-2">
            <div className="flex flex-col">
              <Text size="large" weight="bold">
                {t("common:quoteRequest")}
              </Text>
              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                {quoteRequest.portex_id}
              </Text>
            </div>

            {quoteRequest.deadline_respond_at ? (
              <div className="bg-orange-100 text-red-500 py-2 px-2 rounded-[8px]">
                {t("common:deadlineIn{{time}}", {
                  time: DateTime.fromISO(quoteRequest.deadline_respond_at).toRelative(),
                })}
              </div>
            ) : (
              <div />
            )}
          </div>

          <div className="flex">
            <div className="flex flex-col w-1/2 border-r-2 p-4 space-y-4">
              <Text size="small" weight="bold">
                {t("shipper:shipmentDetails")}
              </Text>

              <div className="flex flex-col space-y-1">
                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                  {t("common:mode")}
                </Text>
                <Text size="small" weight="bold">
                  {quoteRequest.mode ? t(`common:modeEnum.${quoteRequest.mode}`) : EM_DASH}
                </Text>
              </div>

              <div className="flex flex-col space-y-1">
                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                  {t("shipper:numOfTrucks")}
                </Text>
                <Text size="small" weight="bold">
                  {quoteRequest.load_spec?.truck_quantity ? quoteRequest.load_spec.truck_quantity : EM_DASH}
                </Text>
              </div>

              <div className="flex flex-col space-y-1">
                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                  {t("common:equipment")}
                </Text>
                <Text size="small" weight="bold">
                  {quoteRequest.load_spec?.trailer_size
                    ? t(`common:trailerSizes.${quoteRequest.load_spec?.trailer_size}`)
                    : EM_DASH}{" "}
                  {quoteRequest.load_spec?.trailer_type
                    ? getTrailerTypeCopy(quoteRequest.load_spec.trailer_type as TrailerTypeEnum)
                    : EM_DASH}
                </Text>
              </div>

              <div className="flex flex-col space-y-1">
                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                  {t("common:packingType")}
                </Text>
                <Text size="small" weight="bold">
                  {quoteRequest.load_spec?.is_palletized ? t("shipper:palletized") : t("shipper:floorLoaded")}
                </Text>
              </div>

              <div className="flex flex-col space-y-1">
                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                  {t("shipper:driverPreference")}
                </Text>
                <Text size="small" weight="bold">
                  {quoteRequest.load_spec?.driver_preference
                    ? mapDriverPreferenceToCopy[quoteRequest.load_spec?.driver_preference]
                    : EM_DASH}
                </Text>
              </div>

              <div className="flex flex-col space-y-1">
                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                  {t("common:attachments")}
                </Text>
                <Text size="small" weight="bold">
                  <AttachmentsListContainer showDashOnNoAttachments />
                </Text>
              </div>
            </div>

            <div className="flex flex-col w-1/2 p-4 space-y-3">
              <Text size="small" weight="bold">
                {t("shipper:locations")}
              </Text>

              {quoteRequest.stops?.map((stop, index) => (
                <div key={index} className="flex flex-col space-y-3">
                  <Text
                    size="small"
                    weight="bold"
                    typographyProps={{ style: { color: index === 0 ? portexColor.blue500 : portexColor.green500 } }}
                  >
                    {index === 0
                      ? t("common:pickup")
                      : index === (quoteRequest.stops?.length ?? 0) - 1
                      ? t("common:delivery")
                      : t("common:stopLabel", { no: index })}
                  </Text>
                  {stop.address && (
                    <>
                      <Text size="small" weight="bold">
                        {formatAddress.fromSnakeCase(stop.address, "short")}
                      </Text>
                      <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                        {stop.address?.name && (
                          <>
                            {stop.address?.name}
                            <br />
                          </>
                        )}
                        {formatAddress.fromSnakeCase(stop.address, "long")}
                      </Text>
                      {stop.start && (
                        <div className="flex items-center">
                          <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                          <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                            {DateTime.fromISO(stop.start, {
                              zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                            }).toFormat("cccc, MMM d")}
                            {!!stop.start && (
                              <span>
                                {" at "}
                                {DateTime.fromISO(stop.start, {
                                  zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                }).toFormat("h:mm a")}
                                {!!stop.end && stop.end !== stop.start && (
                                  <span>
                                    {" - "}
                                    {DateTime.fromISO(stop.end, {
                                      zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                    }).toFormat("h:mm a")}
                                  </span>
                                )}
                              </span>
                            )}
                          </Text>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Paper>
      </Paper>
    </div>
  );
};

export default EmailPreviewSection;
